import { create } from 'zustand'
import {
  handleGetCoinData, handleGetPriceData, handleFormatCoinList,
  handleGetUserBalanceBySymbol, handleGetUserDToken,
  handleGetUserLendData, hadleFormatUserCoinStatkeStatus,
  handleGetDTokenExchange, handleGetLastCompoundedInterest,
  handleGetDailyEarn,
} from '@/lib/lend-data'
import {
  getFarms, getFarmGlobal, formatLendApr,
  type FarmPool, type FarmGlobal,
} from '@/lib/farm-data'
import { toFixed, decmul } from '@/lib/decimal'

interface LendState {
  marketList: any[]
  farms: FarmPool[]
  farmsGlobal: FarmGlobal
  fetchMarketList: () => Promise<void>
  fetchLendFarmApr: () => Promise<void>
  fetchWalletBalance: (accountName: string) => Promise<void>
  fetchUserLendData: (accountName: string) => Promise<void>
}

export const useLendStore = create<LendState>()((set, get) => ({
  marketList: [],
  farms: [],
  farmsGlobal: { total_weight: 0 },

  fetchMarketList: async () => {
    const [coinData, priceList] = await Promise.all([
      handleGetCoinData(),
      handleGetPriceData(),
    ])
    const oldList = get().marketList
    const list = handleFormatCoinList(coinData, priceList, oldList)
    set({ marketList: list })
  },

  fetchLendFarmApr: async () => {
    const marketList = get().marketList
    const [farmList, global] = await Promise.all([
      getFarms('lendmine.ttr', marketList),
      getFarmGlobal('lendmine.ttr'),
    ])
    const formatted = formatLendApr(global, farmList)

    // Map APR back to market list
    const updatedList = marketList.map((item: any) => {
      const depositFarm = formatted.find(
        (f) => f.token?.sym?.split(',')[1] === 'D' + item.symbol,
      )
      const borrowFarm = formatted.find(
        (f) => f.token?.sym?.split(',')[1] === 'L' + item.symbol,
      )
      return {
        ...item,
        apr: depositFarm?.apr || 0,
        borrowApr: borrowFarm?.apr || 0,
      }
    })

    set({ marketList: updatedList, farms: formatted, farmsGlobal: global })
  },

  fetchWalletBalance: async (accountName: string) => {
    const marketList = get().marketList
    const updatedList = await Promise.all(
      marketList.map(async (item: any) => {
        const balance = await handleGetUserBalanceBySymbol(item.symbol, item.contract, accountName)
        return { ...item, walletBalance: balance }
      }),
    )
    set({ marketList: updatedList })
  },

  fetchUserLendData: async (accountName: string) => {
    const marketList = get().marketList
    const nowSecond = Math.floor(Date.now() / 1000)

    // Fetch user deposit tokens (dTokens)
    const dTokenList = await handleGetUserDToken(accountName)
    // Format collateral status
    await hadleFormatUserCoinStatkeStatus(marketList, accountName)
    // Fetch borrow data
    const borrowList = await handleGetUserLendData(accountName)

    // Build exchange rate map and calculate deposit balances
    const updatedList = await Promise.all(
      marketList.map(async (item: any) => {
        const updated = { ...item }
        const exchangeRate = await handleGetDTokenExchange(item, nowSecond)
        updated.exchangeRate = exchangeRate

        // Find matching dToken balance
        const dToken = dTokenList.find(
          (d: any) => d.balance.split(' ')[1] === 'D' + item.symbol,
        )
        if (dToken) {
          const dtokenBalance = parseFloat(dToken.balance.split(' ')[0])
          updated.dtokenBalance = dtokenBalance
          updated.depositBalance = toFixed(parseFloat(decmul(dtokenBalance, exchangeRate)), item.decimal)
        } else {
          updated.dtokenBalance = 0
          updated.depositBalance = 0
        }

        // Find matching borrow data
        const borrow = borrowList.find(
          (b: any) => b.principal_borrow_balance.split(' ')[1] === item.symbol,
        )
        if (borrow) {
          updated.user_last_variable_borrow_cumulative_index = borrow.last_variable_borrow_cumulative_index
          updated.user_last_update_time = borrow.last_update_time
          updated.principal_borrow_balance = borrow.principal_borrow_balance.split(' ')[0]
          updated.compounded_interest = borrow.compounded_interest || 0
          updated.compounded_borrow_balance = borrow.compounded_borrow_balance || 0

          const { borrowCompound } = handleGetLastCompoundedInterest(updated, nowSecond)
          updated.borrowCompound = borrowCompound
        } else {
          updated.borrowCompound = 0
          updated.principal_borrow_balance = 0
        }

        updated.dailyEarnings = handleGetDailyEarn(updated)
        return updated
      }),
    )

    set({ marketList: updatedList })
  },
}))
